import { useEffect, useRef, useState } from 'react';
import useApi from '@/hooks/useApi';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90_000;

interface UseWebidStatusPollArgs {
  workflowId: string | undefined;
  /** Skip polling entirely (e.g. WebID returned a non-success outcome). */
  enabled: boolean;
}

interface WebidStatusPollState {
  verified: boolean;
  timedOut: boolean;
}

/**
 * Polls `sign.webid.status` until the webhook flips the guest signer's
 * webidVerified flag, or until POLL_TIMEOUT_MS elapses. Deliberately polls the
 * dedicated status endpoint (not sign.start.get): webid/start released the
 * signer out of SIGNING, which sign.start.get rejects.
 */
const useWebidStatusPoll = ({
  workflowId,
  enabled,
}: UseWebidStatusPollArgs): WebidStatusPollState => {
  const [verified, setVerified] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [getWebidStatus] = useApi('sign.webid.status');

  const startedAtRef = useRef<number | null>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!enabled || !workflowId || doneRef.current) {
      return;
    }
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }

    const interval = setInterval(() => {
      void (async () => {
        if (doneRef.current) {
          return;
        }
        if (
          Date.now() - (startedAtRef.current ?? Date.now()) >
          POLL_TIMEOUT_MS
        ) {
          doneRef.current = true;
          clearInterval(interval);
          setTimedOut(true);
          return;
        }
        const res = await getWebidStatus({ workflowId });
        if (
          res?.response?.success &&
          res.response.data.webidVerified &&
          !doneRef.current
        ) {
          doneRef.current = true;
          clearInterval(interval);
          setVerified(true);
        }
      })();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled, workflowId, getWebidStatus]);

  return { verified, timedOut };
};

export default useWebidStatusPoll;
